import React, {useState} from 'react';
import Stack from '@mui/material/Stack';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart, pieArcLabelClasses} from '@mui/x-charts/PieChart';
import Toggle from 'react-toggle'
import "react-toggle/style.css" 
export default ({ selectedOtherInterval, setSelectedOtherInterval, countryList, ishowToggleCountry, setIsShowToggleCountry }) => {

  // Define month labels
  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Function to convert monthly data into chart data
  const convertMonthlyToChartData = (rawData) => {
    if (!rawData) {
      return { data: [], labels: [] }; // Return empty data and labels if rawData is undefined
    }

    const data = monthLabels.map(label => {
      if (rawData.hasOwnProperty(label)) {
        const counts = Object.entries(rawData[label])?.map(([country, count]) => ({ country, count }));
        const totalCount = counts.reduce((acc, { count }) => acc + count, 0);
        return { label, totalCount, counts };
      } else {
        return { label, totalCount: 0, counts: [] };
      }
    });

    return { data, labels: monthLabels };
  };

// Function to convert yearly data into chart data
const convertYearlyToChartData = (rawData) => {
  if (!rawData) {
    return { data: [], labels: [] }; // Return empty data and labels if rawData is undefined
  }

  // Get the current year
  const currentYear = new Date().getFullYear();

  // Generate labels for the latest 5 years starting from the current year in ascending order
  const labels = Array.from({ length: 5 }, (_, index) => currentYear + index).map(year => year.toString());

  // Extract data for each year
  const data = labels.map(year => {
    // If rawData does not have data for the current year, return an empty object
    if (!rawData.hasOwnProperty(year)) {
      return { label: year, totalCount: 0, counts: [] };
    }

    const counts = Object.entries(rawData[year])?.map(([country, count]) => ({ country, count }));
    const totalCount = counts.reduce((acc, { count }) => acc + count, 0);
    return { label: year, totalCount, counts };
  });

  return { data, labels };
};

  const getDayLabels = () => {
    const today = new Date();
    const dayLabels = [];
    for (let i = 0; i <= 31; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dayLabels.push(`Day ${date.getDate()}`);
    }
    return dayLabels.reverse(); // Reverse the labels to show them in ascending order (Day 1, Day 2, ...)
  };


// Define day labels
const dayLabels = getDayLabels();

  // Function to convert daily data into chart data for the previous 31 days including today
  const convertDailyToChartData = (rawData) => {
    if (!rawData) {
      return { data: [], labels: [] }; // Return empty data and labels if rawData is undefined
    }

    const labels = dayLabels;
    const data = labels.length> 0 && labels?.map((label, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (labels?.length - index - 1));
      const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const counts = Object.entries(rawData[dateString] || {}).map(([country, count]) => ({ country, count }));
      const totalCount = counts.reduce((acc, { count }) => acc + count, 0);
      return { label, totalCount, counts };
    });

    return { data, labels };
  };
  // Function to get start and end dates for each week
  const getWeeklyLabels = () => {
    const today = new Date();
    const labels = [];
    let startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 6); // Start from 7 days ago
    let endDate = new Date(today);
    for (let i = 1; i <= 7; i++) {
      const startOfWeek = new Date(startDate);
      const endOfWeek = new Date(endDate);
      labels.push(`Week ${i}`);
      startDate.setDate(startDate.getDate() - 7); // Move to the previous week's start date
      endDate.setDate(endDate.getDate() - 7); // Move to the previous week's end date
    }
    return labels; // Return the labels in ascending order (Week 1, Week 2, ...)
  };
  // Define weekly labels
  const weeklyLabels = getWeeklyLabels();

  // Function to convert weekly data into chart data
  const convertWeeklyToChartData = (rawData) => {
    if (!rawData) {
      return { data: [], labels: [] }; // Return empty data and labels if rawData is undefined
    }

    const labels = weeklyLabels;
    const data =  labels.length> 0 && labels?.map((label, index) => {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - (index + 1) * 7); // Calculate start date of current week
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6); // Calculate end date of current week
      const startDateString = `${startOfWeek.getFullYear()}-${(startOfWeek.getMonth() + 1).toString().padStart(2, '0')}-${startOfWeek.getDate().toString().padStart(2, '0')}`;
      const endDateString = `${endOfWeek.getFullYear()}-${(endOfWeek.getMonth() + 1).toString().padStart(2, '0')}-${endOfWeek.getDate().toString().padStart(2, '0')}`;
      const counts = Object.entries(rawData || {}).filter(([date]) => date >= startDateString && date <= endDateString).flatMap(([_, countryCounts]) => Object.entries(countryCounts).map(([country, count]) => ({ country, count })));
      const totalCount = counts.reduce((acc, { count }) => acc + count, 0);
      return { label, totalCount, counts };
    });

    return { data, labels };
  };
  // Convert nationality data to chart data based on the selected interval
  const nationalityChartData = selectedOtherInterval === 'yearly' ? convertYearlyToChartData(countryList?.nationality) : selectedOtherInterval === 'daily' ? convertDailyToChartData(countryList?.nationality) :   selectedOtherInterval === 'weekly' ? convertWeeklyToChartData(countryList?.nationality) : convertMonthlyToChartData(countryList?.nationality);

  // Initialize series data
  let series = [];

  // Iterate over each entry in the data array
  nationalityChartData?.data?.forEach(dataEntry => {
    // Iterate over each count in the counts array for the current entry
    dataEntry?.counts?.forEach(countData => {
      // Find the index of the series entry corresponding to the current country
      let seriesIndex = series?.findIndex(entry => entry.label === countData.country);
      if (seriesIndex === -1) {
        // If the country is not already present in the series, add a new entry
        series.push({
          data: Array(nationalityChartData.labels.length).fill(0), // Initialize data array with zeros for all months/years
          totalCount: 0, // Initialize totalCount to zero
          label: countData?.country, // Set the label to the country name
          interval: dataEntry?.label,
          color:"#02B2AF"
        });
        seriesIndex = series?.length - 1; // Update seriesIndex to the index of the newly added entry
      }
      // Update the data and totalCount for the current entry
      const dataIndex = nationalityChartData.labels.findIndex(label => label === dataEntry.label);
      if (dataIndex !== -1) {
        series[seriesIndex].data[dataIndex] = countData?.count;
        series[seriesIndex].totalCount += countData?.count;
      }
    });
  });

  const barChartsParams = {
    xAxis: [
      {
        data: nationalityChartData?.labels,
        scaleType: 'band'
      }
    ],
    series: series,
    margin: { top: 10, right: 10 },
    height: 300,
    // width:520,
    slotProps: {
      legend: {
        hidden: true,
      },
    },
  };
  const sizing = {
    margin: { right: 5 },
    // width: 250, // Adjust width as needed
    height: 280, // Adjust height as needed
    legend: { hidden: true },
  };

  const toogleMatchePriceShow = () => {
    setIsShowToggleCountry(!ishowToggleCountry)
  }
  //for pie chart
  function convertData(originalData) {
    let convertedData = {
      data: []
    };
  
    originalData?.forEach((value, i) => {
      value?.data?.forEach((item, j) => {
        convertedData.data.push({ value: item , label: `${value.label} ${value.interval}`, color: value.color });
      });
    });
  
    return convertedData;
  }
  
  const convertedData = convertData(series);
    
  return (
    <>
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="d-flex align-items-center justify-content-between">
                  <h4 className=""><b>{!ishowToggleCountry ? 'Matches played by country' : 'Match earnings as per country'}</b></h4>
                  <div className="d-flex align-items-center justify-content-between">
                      <Toggle
                          className="form-control"
                          id='cheese-status'
                          defaultChecked={ishowToggleCountry}
                          onChange={toogleMatchePriceShow} 
                        />
                  </div>
                </div>
              </div>
                {series && series.length > 0 &&
                <div className="row">
                  <div className="col-md-6">
                    <Stack direction="column">
                      <BarChart {...barChartsParams} />
                    </Stack>
                  </div>
                  <div className="col-md-6">
                  <PieChart
                        series={[
                          {
                            arcLabel: (item) => `${item.label} (${item.value})`,
                            arcLabelMinAngle: 45,
                            data : convertedData?.data
                          },
                        ]}
                        sx={{
                          [`& .${pieArcLabelClasses.root}`]: {
                            fill: 'white',
                            fontSize: 10,
                          },
                        }}
                        {...sizing}
                      />
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );

}
